import React from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import { Autocomplete, AutocompleteRenderInputParams, Button, DialogActions, DialogContent, Paper, Stack, TextField, Typography } from '@mui/material';
import { state, ui } from '../../../../../../../interfaces/interfaces';
import job from '../../../../../../../utilities/objects/job';
import { fullReset, inputComponentLogger } from '../../../../../../../utilities/general';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { useAppSelector } from "@store/hooks";
import { dispatchUILoaded, dispatchUILoading } from "@reducers/uiReducer";
import { RightioWBSAPIModelsOtherPhoneCalls } from '../../../../../../../utilities/apis/wbs/model';

const name = "FailFeedbackDialog";

const handleCancel = () => {
    ui.FailFeedbackDialogModal = false
    ui.FailFeedbackValue = ''
    ui.FailFeedbackApplianceIssue = false
};

const handleClose = () => {
    ui.FailFeedbackDialogModal = false
};

const handleFail = async (applianceType: string, appliance: string, postcodeArea: string, navigate: NavigateFunction) => {
    state.JobStatusId = 0;
    state.JobFailedId = 0;
    state.SavingJob = true;

    if (ui.FailFeedbackApplianceIssue) {

        if (applianceType === 'Other') {
            applianceType = `Other - ${appliance}`;
        }

        const prevFeedbackValue = ui.FailFeedbackValue;

        ui.FailFeedbackValue = `Appliance: ${applianceType}\nPostcode Area: ${postcodeArea}` + (prevFeedbackValue.length > 0 ? `\n${prevFeedbackValue}` : "");
    }

    dispatchUILoading();

    const result = await job.saveFailed();

    dispatchUILoaded();
    if (result) {
        handleClose();
        await fullReset(navigate, "Booking Failed");
    }
    else {
        ui.ProblemMsg = "Error saving job"
        ui.ProblemMsgVariant = "error"
        ui.ProblemShowMsg = true
        state.JobStatusId = 99;
        state.SavingJob = false;
    }
};

type Props = {}

const FailFeedbackDialog: React.FC<Props> = () => {
    const failFeedbackDialogModal = useAppSelector(({ ui }) => ui.FailFeedbackDialogModal);
    const failFeedbackValue = useAppSelector(({ ui }) => ui.FailFeedbackValue);
    const failFeedbackReason = useAppSelector(({ ui }) => ui.FailFeedbackReason) as RightioWBSAPIModelsOtherPhoneCalls;
    const failFeedbackApplianceIssue = useAppSelector(({ ui }) => ui.FailFeedbackApplianceIssue);
    const [postcodeArea, setPostcodeArea] = React.useState<string>("");
    const [applianceType, setApplianceType] = React.useState<string>("");
    const [appliance, setAppliance] = React.useState<string>("");

    React.useEffect(() => {
        if (failFeedbackDialogModal) {
            setPostcodeArea("");
            setApplianceType("");
            setAppliance("");
        }
    }, [failFeedbackDialogModal]);


    const navigate = useNavigate();

    return (
        <Dialog onClose={handleClose} aria-labelledby="simple-dialog-title" open={failFeedbackDialogModal} maxWidth="sm" fullWidth>
            <DialogTitle id="simple-dialog-title">Please provide feedback</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <Paper elevation={4}>
                        <Typography variant="body1" sx={{ fontWeight: "bold", p: 1 }}>Please provide feedback for the failed booking.</Typography>
                    </Paper>
                    {failFeedbackReason?.question && <Paper elevation={4}>
                        <Typography variant="body1" sx={{ fontWeight: "bold", p: 1 }}>{failFeedbackReason?.question}</Typography>
                    </Paper>}
                    {failFeedbackApplianceIssue && <Paper elevation={4}>
                        <Autocomplete
                            id="name"
                            fullWidth
                            options={["Washing Machine", "Dishwasher", "Oven", "Fridge", "Freezer", "Microwave", "Other"]}
                            inputValue={applianceType}
                            onInputChange={(e, v) => setApplianceType(v)}
                            sx={{ p: 1 }} renderInput={(params: AutocompleteRenderInputParams) => <TextField {...params} label="Appliance type" variant="outlined" error={applianceType.length === 0} onBlur={(e) => inputComponentLogger(`${"[App]"}▷[${name}]▷ApplianceType`, e.target.value)} />}
                        />
                    </Paper>}
                    {failFeedbackApplianceIssue && applianceType === 'Other' && <Paper elevation={4}>
                        <TextField
                            autoFocus
                            margin="dense"
                            id="name"
                            label="Appliance"
                            type="text"
                            fullWidth
                            variant="outlined"
                            error={appliance.length === 0}
                            onChange={(e) => setAppliance(e.target.value)}
                            value={appliance}
                            onBlur={(e) =>
                                inputComponentLogger(`${"[App]"}▷[${name}]▷Appliance`, e.target.value)
                            }
                            sx={{ p: 1 }}
                        />
                    </Paper>}
                    {failFeedbackApplianceIssue && <Paper elevation={4}>
                        <TextField
                            autoFocus
                            margin="dense"
                            id="name"
                            label="Postcode Area (1st part only) or N/A if customer does not want to disclose"
                            type="text"
                            fullWidth
                            variant="outlined"
                            error={postcodeArea.length === 0}
                            onChange={(e) => setPostcodeArea(e.target.value)}
                            value={postcodeArea}
                            onBlur={(e) =>
                                inputComponentLogger(`${"[App]"}▷[${name}]▷PostcodeArea`, e.target.value)
                            }
                            sx={{ p: 1 }}
                        />
                    </Paper>}
                    <Paper elevation={4}>
                        <TextField
                            autoFocus
                            margin="dense"
                            id="name"
                            label="Fail reason"
                            type="text"
                            fullWidth
                            variant="outlined"
                            error={failFeedbackValue.length === 0}
                            multiline
                            onChange={(e) => ui.FailFeedbackValue = e.target.value}
                            value={failFeedbackValue}
                            rows={4}
                            onBlur={(e) =>
                                inputComponentLogger(`${"[App]"}▷[${name}]▷FailReason`, e.target.value)
                            }
                            sx={{ p: 1 }}
                        />
                    </Paper>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button color='success' variant='outlined' onClick={handleCancel}>Cancel</Button>
                <Button color='error' variant='contained' onClick={() => handleFail(applianceType, appliance, postcodeArea, navigate)} disabled={failFeedbackValue.length === 0 || (failFeedbackApplianceIssue && (postcodeArea.length === 0 || applianceType.length === 0 || (applianceType === 'Other' && appliance.length === 0)))}>Fail</Button>
            </DialogActions>
        </Dialog>
    );
}

FailFeedbackDialog.displayName = name;

export default React.memo(FailFeedbackDialog);